// src/pages/auth/MfaResetRequestPage.tsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authApi } from '@/services/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import toast from 'react-hot-toast';

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type FormData = z.infer<typeof schema>;

const MfaResetRequestPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      await authApi.mfaRequestReset(data.email);
      setEmailSent(true);
      toast.success('If an account with MFA exists, a reset link has been sent.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="mx-auto w-16 h-16 bg-success rounded-full flex items-center justify-center shadow-lg">
            <CheckCircle className="w-8 h-8 text-success-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Check Your Email</h1>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">
              If an account with MFA enabled exists for <strong>{getValues('email')}</strong>, we've sent instructions to reset your two-factor authentication.
            </p>
            <div className="mt-4">
              <Link to="/login">
                <Button variant="outline" className="w-full">Back to Sign In</Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
            <KeyRound className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-foreground">Reset Two-Factor Auth</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your email to receive a link to reset your authenticator
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label="Email Address"
              type="email"
              autoComplete="email"
              leftIcon={<Mail className="w-4 h-4 text-muted-foreground" />}
              placeholder="Enter your email"
              error={errors.email?.message}
              {...register('email')}
            />
            <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
              Send Reset Link
            </Button>
            <div className="text-center">
              <Link to="/login" className="inline-flex items-center text-sm text-primary hover:text-primary/80">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Sign In
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default MfaResetRequestPage;
